import * as suretyQuotePreview_dto from './preview/surety-quote-preview.dto.mjs';
export { suretyQuotePreview_dto as Preview };
import * as suretyQuote_dto_mixins from './surety-quote.dto.mixins.mjs';
export { suretyQuote_dto_mixins as Mixins };
import * as z from 'zod';
import { suretyBondFormTypeSchema, contractDamageSchema, contractHazardSchema, usStateOrTerritorySchema, SuretyType, bidBondAmountTypeSchema, FileType, finalContractSuretyBondFormTypeSchema, ContractSuretyType, BidBondAmountType, suretyBondIssuanceMethodSchema, suretyQuoteUnderwritingStatusSchema, SuretyQuoteStatus, closedReasonSchema, CLOSED_REASON_NOTE_MAX_LENGTH } from '../../../enums.mjs';
import { addressSchema } from '../../../types/address/address.mjs';
import { Currency } from '../../../types/currency/currency.mjs';
import { naicsCodeRowSchema } from '../../../types/naics-codes/naics-codes.mjs';
import { companyContactId, suretyBondFormTemplateId, naicsCodeId, city, percentSchemaV2, isoDateSchema, suretyBondId, suretyBondIssuanceId, suretyBondFormId, rateSchemaV2, suretyAccountId, isoDateTimeSchema, suretyQuoteId, carrierName } from '../../../types/opaque.mjs';
import { bondFormFileSchema, uploadedFileSchema, safeNumberSchema, currencySchema, positiveCurrencySchema } from '../../../types/utils.mjs';
import { validateCloseRequiredFields } from '../../../utils/validate-close-required-fields.util.mjs';
import { Contract } from './contract/contract-surety-quote.dto.mjs';

var Create;
(function (Create) {
    const principalSchema = z.object({
        id: companyContactId,
        name: z.string(),
    });
    const bondFormSchema = z.union([
        z.object({ templateId: suretyBondFormTemplateId }),
        z.object({
            upload: z.object({
                type: suretyBondFormTypeSchema,
                files: z.array(bondFormFileSchema.extend({
                    typesConfirmedByAdmin: z.boolean().default(false),
                })),
            }),
        }),
        z.undefined(),
    ]);
    const additionalInfoSchema = z.object({
        comment: z.string().nullish(),
        attachments: z.array(uploadedFileSchema()),
    });
    Create.projectSchema = z.object({
        name: z.string().nullish(),
        projectNumber: z.string().nullish(),
        description: z.string().nullish(),
        address: z
            .object({
            city: city.nullish().meta({ partial: true }),
            state: usStateOrTerritorySchema.nullish().meta({ partial: true }),
        })
            .nullish(),
        scopeOfWork: z.array(naicsCodeId).optional().meta({ partial: true }),
        monthsToComplete: safeNumberSchema.min(1).nullish().meta({ partial: true }),
        contractWarrantyMonths: safeNumberSchema.nonnegative().nullish().meta({ partial: true }),
        contractHazards: z.array(contractHazardSchema).optional().meta({ partial: true }),
        contractDamages: z.array(contractDamageSchema).optional().meta({ partial: true }),
    });
    const baseCreateFields = {
        bondAmount: currencySchema,
        completionBond: z.boolean().nullish().meta({ partial: true }),
        suretyType: z.literal(SuretyType.contract),
        principal: principalSchema,
        tListingRequired: z.boolean().nullish().meta({ partial: true }),
        bondForm: bondFormSchema.nullish().meta({ partial: true }),
        additionalInfo: additionalInfoSchema,
    };
    const { completionBond: _completionBond, ...baseCreateFieldsForBidToFinal } = baseCreateFields;
    const principalSchemaForAccountCreation = z.object({
        id: companyContactId.nullish(),
        name: z.string(),
    });
    Create.bidQuoteCreateSchema = z.object({
        ...baseCreateFields,
        contractSuretyType: z.literal(ContractSuretyType.bid),
        bidBondAmountType: bidBondAmountTypeSchema,
        bondAmountPercentOfBid: percentSchemaV2,
        bidAmount: currencySchema,
        bidDate: isoDateSchema.nullish().meta({ partial: true }),
        finalBondFormType: finalContractSuretyBondFormTypeSchema.nullish().meta({ partial: true }),
        project: Create.projectSchema,
        files: uploadedFileSchema([FileType.bid_package]).array().nullish().meta({ partial: true }),
    });
    Create.finalQuoteCreateSchema = z.object({
        ...baseCreateFields,
        contractSuretyType: z.literal(ContractSuretyType.final),
        contractAmount: currencySchema,
        project: Create.projectSchema,
        files: uploadedFileSchema([FileType.contract]).array().nullish().meta({ partial: true }),
    });
    Create.bidToFinalCreateSchema = z.object({
        ...baseCreateFieldsForBidToFinal,
        ...z
            .object({ ...Create.finalQuoteCreateSchema.shape })
            .omit({ contractSuretyType: true, project: true, completionBond: true }).shape,
        contractSuretyType: z.literal(ContractSuretyType.bid_to_final),
        project: Create.projectSchema.optional(),
        actualBidAmount: positiveCurrencySchema,
        actualBidDate: isoDateSchema.nullish().meta({ partial: true }),
        secondBid: positiveCurrencySchema.nullish().meta({ partial: true }),
        contractDate: isoDateSchema.nullish().meta({ partial: true }),
        bidBondId: suretyBondId,
    });
    Create.relaxedMinimalAmountFields = {
        bondAmount: currencySchema.nullish().transform((v) => v ?? Currency.ZERO),
        bondAmountPercentOfBid: percentSchemaV2
            .nullish()
            .transform((v) => v ?? percentSchemaV2.parse(0)),
        bidAmount: currencySchema.nullish().transform((v) => v ?? Currency.ZERO),
        contractAmount: currencySchema.nullish().transform((v) => v ?? Currency.ZERO),
        bidBondAmountType: bidBondAmountTypeSchema
            .nullish()
            .transform((v) => v ?? BidBondAmountType.percent),
    };
    Create.requiredMinimalProjectSchema = z.object({
        ...Create.projectSchema.shape,
        name: z.string().nullish().pipe(z.string().trim().min(1, "Required")),
    });
    Create.minimalBidQuoteFieldOverrides = {
        bondAmount: Create.relaxedMinimalAmountFields.bondAmount,
        bondAmountPercentOfBid: Create.relaxedMinimalAmountFields.bondAmountPercentOfBid,
        bidAmount: currencySchema.nullish().transform((v, ctx) => {
            if (v == null || v <= 0) {
                ctx.addIssue({ code: "custom", message: "Required" });
                return z.NEVER;
            }
            return v;
        }),
        bidBondAmountType: Create.relaxedMinimalAmountFields.bidBondAmountType,
        project: Create.requiredMinimalProjectSchema,
    };
    Create.minimalFinalQuoteFieldOverrides = {
        bondAmount: Create.relaxedMinimalAmountFields.bondAmount,
        contractAmount: Create.relaxedMinimalAmountFields.contractAmount,
        project: Create.requiredMinimalProjectSchema,
    };
    Create.minimalBidToFinalQuoteFieldOverrides = {
        bondAmount: Create.relaxedMinimalAmountFields.bondAmount,
        contractAmount: Create.relaxedMinimalAmountFields.contractAmount,
    };
    (function (Request) {
        Request.quoteSchema = z.discriminatedUnion("contractSuretyType", [
            Create.bidQuoteCreateSchema,
            Create.finalQuoteCreateSchema,
            Create.bidToFinalCreateSchema,
        ]);
        Request.bidQuoteSchemaForAccountCreation = Create.bidQuoteCreateSchema
            .omit({ principal: true })
            .extend({ principal: principalSchemaForAccountCreation });
        Request.finalQuoteSchemaForAccountCreation = Create.finalQuoteCreateSchema
            .omit({ principal: true })
            .extend({ principal: principalSchemaForAccountCreation });
        Request.bidToFinalQuoteSchemaForAccountCreation = Create.bidToFinalCreateSchema
            .omit({ principal: true })
            .extend({ principal: principalSchemaForAccountCreation });
        Request.quoteSchemaForAccountCreation = z.discriminatedUnion("contractSuretyType", [
            Request.bidQuoteSchemaForAccountCreation,
            Request.finalQuoteSchemaForAccountCreation,
            Request.bidToFinalQuoteSchemaForAccountCreation,
        ]);
        Request.schema = z.object({
            quote: Request.quoteSchema,
        });
    })(Create.Request || (Create.Request = {}));
})(Create || (Create = {}));
var Get;
(function (Get) {
    const serverFields = {
        id: suretyQuoteId,
        displayTitle: z.string(),
        createdAt: isoDateTimeSchema,
        updatedAt: isoDateTimeSchema,
        submittedAt: isoDateTimeSchema,
        suretyAccountId: suretyAccountId,
        bondFormId: suretyBondFormId.nullish(),
        bondIssuanceId: suretyBondIssuanceId.nullish(),
    };
    const bidQuoteUnderwrittenFields = {
        finalBondContingentRate: rateSchemaV2.nullish(),
        finalBondContingentRateTemplateVersion: z.string().nullish(),
        finalBondContingentPremium: currencySchema.nullish(),
    };
    const projectGetSchema = Create.projectSchema.extend({
        scopeOfWork: z.array(naicsCodeRowSchema).optional(),
    });
    Get.bidQuoteSchema = Create.bidQuoteCreateSchema
        .omit({ bondForm: true, additionalInfo: true })
        .extend({ ...serverFields, ...bidQuoteUnderwrittenFields, project: projectGetSchema });
    Get.finalQuoteSchema = Create.finalQuoteCreateSchema
        .omit({ bondForm: true, additionalInfo: true })
        .extend({ ...serverFields, project: projectGetSchema });
    Get.bidToFinalQuoteSchema = z
        .object({ ...Create.bidToFinalCreateSchema.shape })
        .omit({ bondForm: true, additionalInfo: true })
        .extend({
        ...serverFields,
        project: projectGetSchema,
        completionBond: z.boolean().nullish().meta({ partial: true }),
    });
    Get.combinedStatus = z.object({
        status: z.enum([
            SuretyQuoteStatus.review,
            SuretyQuoteStatus.approved,
            SuretyQuoteStatus.accepted,
            SuretyQuoteStatus.declined,
            SuretyQuoteStatus.pending,
            SuretyQuoteStatus.bound,
            SuretyQuoteStatus.closed,
        ]),
        carrier: z
            .object({
            name: carrierName,
            displayName: z.string(),
            legalName: z.string(),
            address: addressSchema,
        })
            .optional(),
        underwritingStatus: suretyQuoteUnderwritingStatusSchema.optional(),
        reviewReasons: z.array(z.object({ description: z.string() })),
        supplementalRequirements: z.string().array().optional(),
        agencyCommissionPercentage: percentSchemaV2.optional(),
        rate: rateSchemaV2.optional(),
        premium: currencySchema.optional(),
        expires: isoDateSchema.optional(),
        bondId: suretyBondId.optional(),
        closedReason: z.string().optional(),
        closedNote: z.string().optional(),
        allowedIssuanceMethods: z.array(suretyBondIssuanceMethodSchema).optional(),
    });
    (function (Response) {
        Response.schema = z.discriminatedUnion("contractSuretyType", [
            Get.bidQuoteSchema.extend(Get.combinedStatus.shape),
            Get.finalQuoteSchema.extend(Get.combinedStatus.shape),
            Get.bidToFinalQuoteSchema.extend(Get.combinedStatus.shape),
        ]);
    })(Get.Response || (Get.Response = {}));
})(Get || (Get = {}));
var Update;
(function (Update) {
    (function (Request) {
        Request.schema = z.discriminatedUnion("suretyType", [
            Contract.Update.Request.schema,
        ]);
    })(Update.Request || (Update.Request = {}));
    Update.Bid = Contract.Update.Request.Bid;
    Update.Final = Contract.Update.Request.Final;
    Update.BidToFinal = Contract.Update.Request.BidToFinal;
})(Update || (Update = {}));
var Close;
(function (Close) {
    (function (Request) {
        Request.schema = z
            .object({
            closedReason: closedReasonSchema,
            note: z.string().max(CLOSED_REASON_NOTE_MAX_LENGTH).optional(),
        })
            .superRefine(validateCloseRequiredFields);
    })(Close.Request || (Close.Request = {}));
})(Close || (Close = {}));

export { Close, Create, Get, Update };
